import React, { useState, useEffect, useCallback, useRef } from 'react';

const Footer = ({ setHoverPosition }) => {
    const [particles, setParticles] = useState([]);
    const [isHovering, setIsHovering] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const animationFrameRef = useRef();
    const lastParticleTimeRef = useRef(0);

    // Optimized particle creation with rate limiting
    const createParticle = useCallback((x, y) => {
        const now = performance.now();
        if (now - lastParticleTimeRef.current < 16) return null; // Limit to ~60fps

        lastParticleTimeRef.current = now;
        return {
            id: now,
            x,
            y,
            size: Math.random() * 2.5 + 1, // Smaller particles for better performance
            angle: Math.random() * Math.PI * 2,
            speed: Math.random() * 1.2 + 0.6, // Reduced speed for smoother motion
            life: 100,
            hue: Math.random() * 30 + 210,
            timestamp: now
        };
    }, []);

    // Optimized particle update using requestAnimationFrame
    const updateParticles = useCallback(() => {
        const now = performance.now();

        setParticles(prevParticles => {
            if (prevParticles.length === 0) return prevParticles;

            return prevParticles
                .map(particle => {
                    const deltaTime = (now - particle.timestamp) * 0.016; // Normalize to ~60fps
                    return {
                        ...particle,
                        x: particle.x + Math.cos(particle.angle) * particle.speed * deltaTime,
                        y: particle.y + Math.sin(particle.angle) * particle.speed * deltaTime,
                        life: particle.life - 1.2 * deltaTime,
                        timestamp: now
                    };
                })
                .filter(particle => particle.life > 0);
        });

        animationFrameRef.current = requestAnimationFrame(updateParticles);
    }, []);

    // Smooth mouse tracking
    const handleMouseMove = useCallback((e) => {
        const bounds = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - bounds.left;
        const y = e.clientY - bounds.top;

        setMousePos({ x, y });

        if (isHovering && particles.length < 40) { // Reduced max particles
            const newParticle = createParticle(x, y);
            if (newParticle) {
                setParticles(prev => [...prev, newParticle]);
            }
        }
    }, [isHovering, particles.length, createParticle]);

    // Optimized animation lifecycle
    useEffect(() => {
        if (isHovering) {
            animationFrameRef.current = requestAnimationFrame(updateParticles);
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isHovering, updateParticles]);

    // Smooth link hover effect
    const handleLinkHover = useCallback((e) => {
        const bounds = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - bounds.left - bounds.width / 2) * 0.06;
        const y = (e.clientY - bounds.top - bounds.height / 2) * 0.06;
        e.currentTarget.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }, []);

    return (
        <div className="mt-8 md:mt-16 font-mono text-xs md:text-sm opacity-80">
            <div className="flex flex-col items-center md:flex-row md:items-center md:justify-between px-4 md:px-0">
                <div className="flex items-center space-x-3 md:space-x-4 mb-6 md:mb-0">
                    <a
                        href="mailto:pushpalathanshenoy@gmail.com"
                        className="relative group transition-transform duration-200 ease-out"
                        onMouseMove={handleLinkHover}
                        onMouseLeave={e => e.currentTarget.style.transform = 'translate3d(0, 0, 0)'}
                    >
                        <span className="inline-block transition-all duration-200 ease-out group-hover:-translate-y-0.5 group-hover:text-blue-300">
                            mail
                        </span>
                    </a>
                    <span className="opacity-50">·</span>
                    <a
                        href="https://www.linkedin.com/in/pushpalatha-shenoy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative group transition-transform duration-200 ease-out"
                        onMouseMove={handleLinkHover}
                        onMouseLeave={e => e.currentTarget.style.transform = 'translate3d(0, 0, 0)'}
                    >
                        <span className="inline-block transition-all duration-200 ease-out group-hover:-translate-y-0.5 group-hover:text-blue-400">
                            linkedIn
                        </span>
                    </a>
                </div>

                <div
                    className="relative h-12 md:h-16 w-32 md:w-40 cursor-pointer overflow-hidden rounded-lg transform-gpu"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => {
                        setIsHovering(true);
                        setHoverPosition({ section: "design", index: 0 });
                    }}
                    onMouseLeave={() => {
                        setIsHovering(false);
                        setHoverPosition({ section: null, index: null });
                        setParticles([]);
                    }}
                >
                    <div className="absolute inset-0">
                        {particles.map(particle => (
                            <div
                                key={particle.id}
                                className="absolute rounded-full transform-gpu will-change-transform"
                                style={{
                                    left: particle.x,
                                    top: particle.y,
                                    width: particle.size,
                                    height: particle.size,
                                    backgroundColor: `hsla(${particle.hue}, 85%, 65%, ${particle.life / 100})`,
                                    transform: 'translate(-50%, -50%)',
                                }}
                            />
                        ))}
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                            <span
                                className={`text-sm font-light tracking-wider transform-gpu transition-all duration-200 
                                ${isHovering ? 'opacity-100 scale-105' : 'opacity-70'}`}
                                style={{
                                    textShadow: isHovering ? '0 0 8px rgba(96, 165, 250, 0.4)' : 'none'
                                }}
                            >
                                design
                            </span>
                            <div
                                className={`absolute bottom-0 left-1/2 w-full h-px bg-blue-400 transform-gpu -translate-x-1/2 transition-all duration-200
                                ${isHovering ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}`}
                            />
                        </div>
                    </div>

                    <div
                        className="absolute inset-0 transition-opacity duration-200"
                        style={{
                            background: `radial-gradient(circle at ${mousePos.x}px ${mousePos.y}px, 
                                rgba(96, 165, 250, 0.15) 0%, 
                                rgba(96, 165, 250, 0.05) 20%, 
                                transparent 50%)`,
                            opacity: isHovering ? 1 : 0
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Footer;